import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Lock, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { CleanKinLogo } from "@/components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerAuth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    address: "",
    postcode: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/customer/login`, loginData);
      // Auth token now lives in httpOnly cookie set by backend.
      // Only persist non-sensitive display data + a logged-in flag for UI guards.
      localStorage.setItem("customer_logged_in", "true");
      localStorage.setItem("customer_name", response.data.name);
      localStorage.setItem("customer_id", response.data.id);
      toast.success(`Welcome back, ${response.data.name}!`);
      navigate("/account");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...data } = registerData;
      const response = await axios.post(`${API}/customer/register`, data);
      localStorage.setItem("customer_logged_in", "true");
      localStorage.setItem("customer_name", response.data.name);
      localStorage.setItem("customer_id", response.data.id);
      toast.success("Account created successfully!");
      navigate("/account");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAF9] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/">
            <CleanKinLogo size="default" />
          </Link>
          <p className="text-[#5C6B57] mt-4">Manage your bookings and schedule recurring cleans</p>
        </div>

        <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural">
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Email</Label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1 h-12"
                      required
                      data-testid="customer-login-email"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                      className="mt-1 h-12"
                      required
                      data-testid="customer-login-password"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6 btn-primary"
                    disabled={loading}
                    data-testid="customer-login-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Full Name *</Label>
                    <Input
                      placeholder="John Smith"
                      value={registerData.name}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 h-12"
                      required
                      data-testid="customer-register-name"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Email *</Label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1 h-12"
                      required
                      data-testid="customer-register-email"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-[#2D5016]">Password *</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                        className="mt-1 h-12"
                        required
                        data-testid="customer-register-password"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-[#2D5016]">Confirm *</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="mt-1 h-12"
                        required
                        data-testid="customer-register-confirm"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Phone</Label>
                    <Input
                      type="tel"
                      placeholder="+44 7700 900000"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1 h-12"
                      data-testid="customer-register-phone"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Address</Label>
                    <Input
                      placeholder="123 Main Street, London"
                      value={registerData.address}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, address: e.target.value }))}
                      className="mt-1 h-12"
                      data-testid="customer-register-address"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Postcode</Label>
                    <Input
                      placeholder="SW1A 1AA"
                      value={registerData.postcode}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
                      className="mt-1 h-12"
                      data-testid="customer-register-postcode"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6 btn-primary"
                    disabled={loading}
                    data-testid="customer-register-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#5C6B57] mt-6">
          <Link to="/" className="text-[#2D5016] hover:underline flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default CustomerAuth;
